/**
 * The "Diurnal QC" tab's calls (docs/specs/cloud/diurnal-qc-cloud.md; 20261027008000_diurnal_cloud_qc.sql; Diurnal
 * parity C3): the organisation's TA Live units and the project's base stations, the days Teslyn Cloud worked out,
 * the days shown to the client, publishing and withdrawing, and the side-by-side comparisons. Who may do what is
 * decided by the database; clients read only the days shown to them.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  readCloudQcDay,
  type StationLinkRow,
  type CloudQcDayRow,
  type SideBySideResult,
  type SideBySideSource,
} from '@teslyn/domain';

const fail = (e: { message: string } | null): void => {
  if (e) throw new Error(e.message);
};

export interface DiurnalStation {
  readonly id: string;
  readonly name: string;
  readonly station_number: string;
}

/** The organisation's active TA Live units, in display order (staff only). */
export async function loadStations(sb: SupabaseClient, tenantId: string): Promise<DiurnalStation[]> {
  const { data, error } = await sb
    .from('sensor_station')
    .select('id, name, station_number, display_order')
    .eq('tenant_id', tenantId)
    .eq('active', true)
    .order('display_order');
  fail(error);
  return ((data ?? []) as DiurnalStation[]).map((s) => ({
    id: s.id,
    name: s.name,
    station_number: s.station_number,
  }));
}

/** The units linked to the project, with their dates (decision 788). */
export async function loadLinks(sb: SupabaseClient, projectId: string): Promise<StationLinkRow[]> {
  const { data, error } = await sb
    .from('project_base_station')
    .select('project_id, station_id, starts_on, ends_on')
    .eq('project_id', projectId);
  fail(error);
  return (data ?? []) as StationLinkRow[];
}

/** Every unit-to-project link of the organisation (the Station health page). */
export async function loadTenantLinks(sb: SupabaseClient, tenantId: string): Promise<StationLinkRow[]> {
  const { data, error } = await sb
    .from('project_base_station')
    .select('project_id, station_id, starts_on, ends_on')
    .eq('tenant_id', tenantId)
    .order('starts_on', { ascending: false });
  fail(error);
  return (data ?? []) as StationLinkRow[];
}

/** Link a unit to a project from `starts` (today when null) to `ends` (null: still there), change its dates, or unlink. */
export async function setLink(
  sb: SupabaseClient,
  projectId: string,
  stationId: string,
  on: boolean,
  starts: string | null = null,
  ends: string | null = null,
): Promise<void> {
  const { error } = await sb.rpc('diurnal_station_link', {
    p_project: projectId,
    p_station: stationId,
    p_on: on,
    p_starts: starts,
    p_ends: ends,
  });
  fail(error);
}

const DAY_COLUMNS =
  'id, project_id, station_id, day, offset_h, status, readings, first_at, last_at, final, computed_at, result';

/** The days Teslyn Cloud worked out for the project, newest first (the last 31), without their charts. */
export async function loadDays(
  sb: SupabaseClient,
  projectId: string,
  names: ReadonlyMap<string, string>,
): Promise<CloudQcDayRow[]> {
  const { data, error } = await sb
    .from('diurnal_qc_day')
    .select(DAY_COLUMNS)
    .eq('project_id', projectId)
    .order('day', { ascending: false })
    .limit(31);
  fail(error);
  return ((data ?? []) as Record<string, unknown>[]).map((r) =>
    readCloudQcDay({ ...r, station_name: names.get(String(r.station_id)) ?? 'Base station' }),
  );
}

export interface DiurnalPublication extends CloudQcDayRow {
  readonly published: boolean;
  readonly published_at: string;
  readonly published_by: string | null;
}

/** The days shown to the client (staff: every one still shown; a client: theirs), newest first. */
export async function loadPublications(sb: SupabaseClient, projectId: string): Promise<DiurnalPublication[]> {
  const { data, error } = await sb
    .from('diurnal_qc_publication')
    .select(
      'id, project_id, station_id, station_name, day, offset_h, readings, first_at, last_at, computed_at, result, published, published_at, published_by',
    )
    .eq('project_id', projectId)
    .eq('published', true)
    .order('day', { ascending: false })
    .limit(62);
  fail(error);
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    ...readCloudQcDay({ ...r, status: 'online', final: true }),
    published: r.published === true,
    published_at: String(r.published_at ?? ''),
    published_by: typeof r.published_by === 'string' ? r.published_by : null,
  }));
}

/** One day's chart series (a worked-out day, or a day shown to the client). */
export async function loadChart(
  sb: SupabaseClient,
  table: 'diurnal_qc_day' | 'diurnal_qc_publication',
  id: string,
): Promise<CloudQcDayRow['chart']> {
  const { data, error } = await sb.from(table).select('chart').eq('id', id).maybeSingle();
  fail(error);
  return readCloudQcDay({ chart: (data as { chart?: unknown } | null)?.chart }).chart;
}

/** Show a worked-out day to the client (a copy as it is now); returns the publication's id. */
export async function publishDay(sb: SupabaseClient, dayId: string): Promise<string> {
  const { data, error } = await sb.rpc('diurnal_qc_publish', { p_day: dayId });
  fail(error);
  return String(data);
}

export async function withdrawDay(sb: SupabaseClient, publicationId: string): Promise<boolean> {
  const { data, error } = await sb.rpc('diurnal_qc_withdraw', { p_publication: publicationId });
  fail(error);
  return data === true;
}

// ------------------------------------------------------------------------------------------------ side by side

export interface Comparison {
  readonly id: string;
  readonly station_id: string | null;
  readonly day: string;
  readonly source: SideBySideSource;
  readonly file_name: string;
  readonly verdict: 'same' | 'different';
  readonly note: string;
  readonly compared_at: string;
  readonly compared_by: string | null;
  readonly result: SideBySideResult;
}

export async function loadComparisons(sb: SupabaseClient, projectId: string): Promise<Comparison[]> {
  const { data, error } = await sb
    .from('diurnal_side_by_side')
    .select('id, station_id, day, source, file_name, verdict, note, compared_at, compared_by, result')
    .eq('project_id', projectId)
    .order('day', { ascending: false })
    .limit(200);
  fail(error);
  return (data ?? []) as Comparison[];
}

export async function saveComparison(
  sb: SupabaseClient,
  row: {
    readonly tenant_id: string;
    readonly project_id: string;
    readonly station_id: string;
    readonly day: string;
    readonly source: SideBySideSource;
    readonly file_name: string;
    readonly cloud_computed_at: string;
    readonly result: SideBySideResult;
    readonly verdict: 'same' | 'different';
    readonly note: string;
    readonly compared_by: string;
  },
): Promise<void> {
  const { error } = await sb.from('diurnal_side_by_side').insert(row);
  fail(error);
}

export async function deleteComparison(sb: SupabaseClient, id: string): Promise<void> {
  const { error } = await sb.from('diurnal_side_by_side').delete().eq('id', id);
  fail(error);
}

/** The browser's raw-deflate inflater (for reading .xlsx workbooks). */
export async function browserInflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}
